import type { MoneyDto } from '@robu/shared';

import { cn } from '../../lib/cn';
import { formatMoney, formatSignedMoney, minorOf } from '../../lib/money';

export type MoneySize = 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<MoneySize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base font-semibold',
  xl: 'text-2xl font-semibold tracking-tight',
};

export interface MoneyProps {
  value: MoneyDto | null | undefined;
  /** Prefix credits with "+" — ledger rows need the direction to be explicit. */
  signed?: boolean;
  /** `auto` colours by sign, which is what a balance history wants. */
  tone?: 'auto' | 'inherit' | 'muted' | 'brand' | 'positive' | 'negative';
  size?: MoneySize;
  className?: string;
}

/**
 * The only place money is turned into text. Values arrive as `MoneyDto` and are
 * formatted from `bigint` minor units, so no monetary value is ever coerced
 * into a JavaScript number (TZ §17).
 */
export function Money({
  value,
  signed = false,
  tone = 'inherit',
  size = 'md',
  className,
}: MoneyProps) {
  const minor = minorOf(value);
  const text = signed ? formatSignedMoney(value) : formatMoney(value);

  const TONE_CLASSES = {
    muted: 'text-ink-muted',
    brand: 'text-brand',
    positive: 'text-positive',
    negative: 'text-negative',
    inherit: undefined,
  } as const;

  const toneClass =
    tone === 'auto'
      ? minor < 0n
        ? 'text-negative'
        : minor > 0n
          ? 'text-positive'
          : 'text-ink-muted'
      : TONE_CLASSES[tone];

  return (
    <span
      className={cn('font-display tabular whitespace-nowrap', SIZES[size], toneClass, className)}
    >
      {text}
      <span className="crystal" aria-hidden="true" />
    </span>
  );
}
